/**
 * 键盘事件优先级常量
 */
export const KEYBOARD_PRIORITY = {
  /** 最高优先级：模态框、弹窗等 */
  MODAL: 1,
  /** 高优先级：输入框、编辑器等 */
  INPUT: 10,
  /** 中优先级：快捷短语、工具栏等 */
  TOOLBAR: 50,
  /** 低优先级：页面级功能 */
  PAGE: 100,
  /** 最低优先级：全局快捷键 */
  GLOBAL: 1000,
} as const;

/**
 * 修饰键映射
 */
const MODIFIER_KEYS = {
  ctrl: 'ctrlKey',
  alt: 'altKey',
  shift: 'shiftKey',
  meta: 'metaKey',
} as const;

/**
 * 组合键解析结果
 */
interface KeyCombination {
  modifiers: string[];
  key: string;
}

/**
 * 解析组合键字符串
 * @param keyString 组合键字符串，如 'ctrl+c', 'alt+shift+f1'
 * @returns 解析后的组合键对象
 */
function parseKeyCombination(keyString: string): KeyCombination | null {
  const parts = keyString.toLowerCase().split('+');

  if (parts.length < 2) {
    // 单个按键，不是组合键
    return null;
  }

  const modifiers: string[] = [];
  let key = '';

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();

    if (MODIFIER_KEYS[part as keyof typeof MODIFIER_KEYS]) {
      modifiers.push(part);
    } else if (i === parts.length - 1) {
      // 最后一个部分应该是主键
      key = part;
    } else {
      // 无效的修饰键
      console.warn(`不支持的修饰键: ${part}`);
      return null;
    }
  }

  if (!key) {
    console.warn(`无效的组合键格式: ${keyString}`);
    return null;
  }

  return { modifiers, key };
}

/**
 * 检查组合键是否匹配当前事件
 * @param combination 组合键对象
 * @param event 键盘事件
 * @returns 是否匹配
 */
function isKeyCombinationMatched(combination: KeyCombination, event: KeyboardEvent): boolean {
  // 检查主键是否匹配
  if (
    event?.key?.toLowerCase() !== combination.key &&
    event?.code?.toLowerCase() !== combination.key
  ) {
    return false;
  }

  // 检查修饰键是否匹配
  for (const modifier of combination.modifiers) {
    const modifierKey = MODIFIER_KEYS[modifier as keyof typeof MODIFIER_KEYS];
    if (!event[modifierKey as keyof KeyboardEvent]) {
      return false;
    }
  }

  // 确保没有额外的修饰键被按下
  const allModifiers = Object.keys(MODIFIER_KEYS);
  for (const modifier of allModifiers) {
    if (!combination.modifiers.includes(modifier)) {
      const modifierKey = MODIFIER_KEYS[modifier as keyof typeof MODIFIER_KEYS];
      if (event[modifierKey as keyof KeyboardEvent]) {
        return false;
      }
    }
  }

  return true;
}

/**
 * 键盘事件处理器接口
 */
export interface KeyboardHandler {
  /** 处理器唯一标识 */
  id: string;
  /** 优先级，数字越小优先级越高 */
  priority: number;
  /** 键盘事件类型 */
  eventType: 'keydown' | 'keyup';
  /**
   * 要监听的按键，支持单个按键和组合键
   * 单个按键：['Enter', 'Escape', 'a', '1']
   * 组合键：['ctrl+c', 'alt+tab', 'ctrl+shift+s']
   * 混合使用：['Enter', 'ctrl+s', 'Escape']
   */
  keys: string[];
  /** 事件处理函数 */
  handler: (event: KeyboardEvent) => boolean | void;
  /** 上下文条件函数，返回true时该处理器才生效 */
  context?: () => boolean;
  /** 是否阻止事件冒泡 */
  preventDefault?: boolean;
  /** 是否阻止其他处理器执行 */
  stopPropagation?: boolean;
}

/**
 * 键盘事件管理器
 */
class KeyboardEventManager {
  private handlers: Map<string, KeyboardHandler> = new Map();
  private isListening = false;

  /**
   * 注册键盘事件处理器
   */
  register(handler: KeyboardHandler): void {
    this.handlers.set(handler.id, handler);
    this.startListening();
  }

  /**
   * 注销键盘事件处理器
   */
  unregister(id: string): void {
    this.handlers.delete(id);
    if (this.handlers.size === 0) {
      this.stopListening();
    }
  }

  /**
   * 开始监听键盘事件
   */
  private startListening(): void {
    if (this.isListening) return;

    this.isListening = true;
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
  }

  /**
   * 停止监听键盘事件
   */
  private stopListening(): void {
    if (!this.isListening) return;

    this.isListening = false;
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
  }

  /**
   * 处理keydown事件
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    this.handleEvent(event, 'keydown');
  };

  /**
   * 处理keyup事件
   */
  private handleKeyUp = (event: KeyboardEvent): void => {
    this.handleEvent(event, 'keyup');
  };

  /**
   * 处理键盘事件
   */
  private handleEvent(event: KeyboardEvent, eventType: 'keydown' | 'keyup'): void {
    // 获取所有匹配的处理器，按优先级排序
    const matchedHandlers = Array.from(this.handlers.values())
      .filter((handler) => {
        // 检查事件类型
        if (handler.eventType !== eventType) {
          return false;
        }
        // 检查上下文条件
        if (handler.context && !handler.context()) {
          return false;
        }

        // 检查按键匹配（支持单个按键和组合键）
        return handler.keys.some((key) => {
          const combination = parseKeyCombination(key);

          if (combination) {
            // 组合键匹配
            return isKeyCombinationMatched(combination, event);
          } else {
            // 单个按键匹配
            return (
              key?.toLowerCase() === event.key?.toLowerCase() ||
              key?.toLowerCase() === event.code?.toLowerCase()
            );
          }
        });
      })
      .sort((a, b) => a.priority - b.priority);

    // 按优先级执行处理器
    for (const handler of matchedHandlers) {
      try {
        const result = handler.handler(event);

        // 如果处理器返回true或undefined，继续执行下一个
        // 如果处理器返回false，停止执行后续处理器
        if (result === false) {
          break;
        }

        // 如果设置了阻止事件冒泡，停止执行后续处理器
        if (handler.stopPropagation) {
          break;
        }

        // 如果设置了阻止默认行为
        if (handler.preventDefault) {
          event.preventDefault();
        }
      } catch (error) {
        console.error(`键盘事件处理器 ${handler.id} 执行失败:`, error);
      }
    }
  }

  /**
   * 获取当前注册的处理器列表
   */
  getHandlers(): KeyboardHandler[] {
    return Array.from(this.handlers.values());
  }

  /**
   * 清空所有处理器
   */
  clear(): void {
    this.handlers.clear();
    this.stopListening();
  }
}

// 全局键盘事件管理器实例
export const keyboardManager = new KeyboardEventManager();
